import { useEffect, useState, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  updateProfile,
  changePassword,
  billingMe,
  billingCancel,
  billingReactivate,
  applyVoucher,
  checkoutPayfast,
  checkoutPaypal,
  capturePaypal,
  checkoutEft,
  eftClaim,
  eftCancel,
} from "@/api";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Loader2,
  Save,
  KeyRound,
  CreditCard,
  Building2,
  Sparkles,
  CheckCircle2,
  XCircle,
  RefreshCcw,
  Ticket,
} from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();

  useEffect(() => {
    // Handle return from payment gateways via query params
    const payfast = params.get("payfast");
    const paypal = params.get("paypal");
    const orderId = params.get("token"); // PayPal returns ?token=ORDERID

    function clearParam(...keys) {
      const next = new URLSearchParams(params);
      keys.forEach((k) => next.delete(k));
      setParams(next, { replace: true });
    }

    if (payfast === "success") {
      toast.success("Payment received — your subscription is being activated.");
      setTimeout(() => refresh(), 1500);
      clearParam("payfast");
    } else if (payfast === "cancel") {
      toast.error("Payment canceled — no charges were made.");
      clearParam("payfast");
    }

    if (paypal === "return" && orderId) {
      capturePaypal(orderId)
        .then((r) => {
          if (r.activated) {
            toast.success("Subscription activated. Welcome to Atelier Studio!");
          } else {
            toast.message("Payment is processing — status will update shortly.");
          }
        })
        .catch(() => toast.error("Could not finalize your PayPal payment"))
        .finally(() => {
          refresh();
          clearParam("paypal", "token", "PayerID");
        });
    } else if (paypal === "cancel") {
      toast.error("PayPal checkout canceled — no charges were made.");
      clearParam("paypal", "token", "PayerID");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user || typeof user !== "object") {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-10" data-testid="profile-page">
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold mb-1">
          Account
        </p>
        <h1 className="font-display text-4xl md:text-5xl text-stone-900">
          Your profile
        </h1>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="details" data-testid="profile-tab-details">
            Details
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="profile-tab-security">
            Security
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="profile-tab-billing">
            Billing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <DetailsPanel />
        </TabsContent>
        <TabsContent value="security">
          <SecurityPanel />
        </TabsContent>
        <TabsContent value="billing">
          <BillingPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DetailsPanel() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const u = await updateProfile({ name });
      setUser((prev) => ({ ...prev, name: u.name, avatar: u.avatar }));
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-5 max-w-2xl"
      data-testid="profile-details-panel"
    >
      <div>
        <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
          Full name
        </label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="profile-name"
          className="h-10"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
          Email
        </label>
        <Input
          value={user.email}
          disabled
          data-testid="profile-email"
          className="h-10 bg-stone-50 text-stone-500"
        />
        <p className="text-xs text-stone-500 mt-1">
          Email is locked to keep your account secure.
        </p>
      </div>
      <Button
        onClick={save}
        disabled={busy}
        data-testid="profile-save"
        className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
        ) : (
          <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
        )}
        Save changes
      </Button>
    </div>
  );
}

function SecurityPanel() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!cur || !next) {
      toast.error("Enter your current and new password");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await changePassword({ current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur("");
      setNext("");
      setConfirm("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-5 max-w-2xl"
      data-testid="profile-security-panel"
    >
      <div>
        <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
          Current password
        </label>
        <Input
          type="password"
          value={cur}
          onChange={(e) => setCur(e.target.value)}
          data-testid="password-current"
          className="h-10"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
            New password
          </label>
          <Input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            data-testid="password-new"
            className="h-10"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
            Confirm new
          </label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="password-confirm"
            className="h-10"
          />
        </div>
      </div>
      <Button
        onClick={save}
        disabled={busy}
        data-testid="password-save"
        className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
        ) : (
          <KeyRound className="w-4 h-4 mr-2" strokeWidth={1.5} />
        )}
        Change password
      </Button>
    </div>
  );
}

function BillingPanel() {
  const { user, refresh: refreshAuth } = useAuth();
  const [billing, setBilling] = useState(null);
  const [voucher, setVoucher] = useState("");
  const [busy, setBusy] = useState(false);
  const [eft, setEft] = useState(null);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [plan, setPlan] = useState("monthly"); // monthly | annual

  async function payWithEft() {
    setBusy(true);
    try {
      const r = await checkoutEft(plan, appliedPromo?.code);
      setEft(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function claimEftPaid() {
    setBusy(true);
    try {
      await eftClaim();
      toast.success("Thanks! We'll confirm your payment shortly.");
      setEft(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function cancelEftPayment() {
    setBusy(true);
    try {
      await eftCancel();
      toast.success("EFT payment canceled.");
      setEft(null);
      await load();
      refreshAuth?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function load() {
    try {
      const b = await billingMe();
      setBilling(b);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function payWithPayfast() {
    setBusy(true);
    try {
      const r = await checkoutPayfast(plan, appliedPromo?.code);
      if (r?.url && r?.payload) {
        // Build and auto-submit a hidden form to redirect to PayFast
        const form = document.createElement("form");
        form.method = "POST";
        form.action = r.url;
        Object.entries(r.payload).forEach(([k, v]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = k;
          input.value = String(v);
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
      } else {
        toast.error("Could not start PayFast checkout");
        setBusy(false);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  }

  async function payWithPaypal() {
    setBusy(true);
    try {
      const r = await checkoutPaypal(plan, appliedPromo?.code);
      if (r?.approve_url) {
        window.location.assign(r.approve_url);
      } else {
        toast.error("Could not start PayPal checkout");
        setBusy(false);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  }

  async function cancel() {
    setBusy(true);
    try {
      await billingCancel();
      toast.success("Subscription will end at period end");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function reactivate() {
    setBusy(true);
    try {
      await billingReactivate();
      toast.success("Subscription reactivated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function redeem() {
    if (!voucher.trim()) {
      toast.error("Enter a voucher code");
      return;
    }
    setBusy(true);
    try {
      const r = await applyVoucher(voucher.trim());
      if (r.trial_extended_days) {
        toast.success(`Trial extended by ${r.trial_extended_days} days`);
        setVoucher("");
        setAppliedPromo(null);
        load();
      } else if (r.applies_at_checkout) {
        setAppliedPromo({
          code: r.code,
          original_price: r.original_price,
          discounted_price: r.discounted_price,
          discount_amount: r.discount_amount,
          discount_type: r.discount_type,
          discount_value: r.discount_value,
          currency: r.currency,
        });
        toast.success(`Discount “${r.code}” applied`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!billing) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" strokeWidth={1.5} />
      </div>
    );
  }

  if (user.role === "admin") {
    return (
      <div
        className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 max-w-2xl"
        data-testid="profile-billing-admin"
      >
        <div className="flex items-center gap-2 text-stone-800">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
          <p className="font-medium">You're an admin — billing is unlimited.</p>
        </div>
      </div>
    );
  }

  const status = billing.subscription_status;
  const pay = billing.payment;
  const isActive = status === "active";
  const isTrialing = status === "trialing";
  const isCanceling = billing.cancel_at_period_end;

  const monthlyPrice = Number(pay?.monthly_price ?? pay?.subscription_price ?? 0);
  const annualPrice = Number(pay?.annual_price ?? monthlyPrice * 11);
  const planBase = plan === "annual" ? annualPrice : monthlyPrice;
  const planFinal = (() => {
    if (!appliedPromo) return planBase;
    if (appliedPromo.discount_type === "percent")
      return Math.max(0, planBase * (1 - Number(appliedPromo.discount_value) / 100));
    if (appliedPromo.discount_type === "amount")
      return Math.max(0, planBase - Number(appliedPromo.discount_value));
    return planBase;
  })();
  const planSave = Math.max(0, planBase - planFinal);
  const periodEndStr = billing.current_period_end
    ? new Date(
        typeof billing.current_period_end === "number"
          ? billing.current_period_end * 1000
          : billing.current_period_end
      ).toLocaleDateString()
    : null;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="profile-billing-panel">
      {/* Status card */}
      <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold mb-2">
              Current status
            </p>
            <div className="flex items-center gap-2">
              {isActive ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
              ) : isTrialing ? (
                <Sparkles className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
              ) : (
                <XCircle className="w-5 h-5 text-stone-400" strokeWidth={1.5} />
              )}
              <span className="text-xl text-stone-900 font-display capitalize" data-testid="billing-status">
                {status || "no subscription"}
              </span>
              {billing.subscription_plan && (
                <span className="text-xs text-stone-500 ml-2 uppercase tracking-wider">
                  {billing.subscription_plan}
                </span>
              )}
            </div>
            {isTrialing && typeof billing.trial_days_left === "number" && (
              <p className="text-sm text-stone-600 mt-2" data-testid="billing-trial-days-left">
                {billing.trial_days_left} day{billing.trial_days_left === 1 ? "" : "s"} left in your trial
              </p>
            )}
            {isActive && periodEndStr && (
              <p className="text-sm text-stone-600 mt-2" data-testid="billing-period-end">
                Access until {periodEndStr} · pay again to renew
              </p>
            )}
            {status === "canceled" && periodEndStr && billing.has_access && (
              <p className="text-sm text-stone-600 mt-2" data-testid="billing-canceled-access">
                Canceled — you keep access until {periodEndStr}
              </p>
            )}
          </div>
          {(isActive || isCanceling) && (
            <div>
              {isCanceling ? (
                <Button
                  onClick={reactivate}
                  disabled={busy}
                  data-testid="billing-reactivate"
                  className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
                >
                  <RefreshCcw className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Reactivate
                </Button>
              ) : (
                <Button
                  onClick={cancel}
                  disabled={busy}
                  variant="outline"
                  data-testid="billing-cancel"
                  className="rounded-full"
                >
                  Cancel subscription
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Plan + payment options — shown unless actively subscribed with a valid period */}
      {!(isActive && billing.has_access) && (
        <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-5" data-testid="billing-plan-panel">
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold mb-3">
              Choose a plan
            </p>
            {pay && monthlyPrice > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPlan("monthly")}
                    data-testid="plan-monthly"
                    className={
                      "text-left rounded-xl border p-4 transition-colors " +
                      (plan === "monthly"
                        ? "border-stone-900 bg-stone-50"
                        : "border-stone-200 hover:border-stone-300")
                    }
                  >
                    <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">Monthly</p>
                    <p className="font-display text-2xl text-stone-900 mt-1">
                      {pay.currency} {monthlyPrice.toFixed(2)}
                    </p>
                    <p className="text-xs text-stone-500">per month</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPlan("annual")}
                    data-testid="plan-annual"
                    className={
                      "text-left rounded-xl border p-4 transition-colors relative " +
                      (plan === "annual"
                        ? "border-stone-900 bg-stone-50"
                        : "border-stone-200 hover:border-stone-300")
                    }
                  >
                    <span className="absolute top-3 right-3 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                      1 month free
                    </span>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">Annual</p>
                    <p className="font-display text-2xl text-stone-900 mt-1">
                      {pay.currency} {annualPrice.toFixed(2)}
                    </p>
                    <p className="text-xs text-stone-500">per year · pay 11, get 12</p>
                  </button>
                </div>

                <div className="flex items-baseline gap-2 flex-wrap mt-4" data-testid="billing-price-block">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold w-full">
                    You pay
                  </span>
                  {planSave > 0 && (
                    <span className="font-display text-2xl text-stone-400 line-through" data-testid="billing-price-original">
                      {pay.currency} {planBase.toFixed(2)}
                    </span>
                  )}
                  <span className="font-display text-4xl text-stone-900" data-testid="billing-price">
                    {pay.currency} {planFinal.toFixed(2)}
                  </span>
                  <span className="text-sm text-stone-500">
                    {plan === "annual" ? "/ year" : "/ month"}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-sm text-stone-500">
                A subscription plan hasn't been configured yet. Please check back soon.
              </p>
            )}
            {appliedPromo && (
              <div className="mt-2 inline-flex items-center gap-2 text-xs" data-testid="billing-applied-promo">
                <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                  {appliedPromo.code} · save {pay?.currency} {planSave.toFixed(2)}
                </span>
                <button
                  type="button"
                  onClick={() => setAppliedPromo(null)}
                  className="text-stone-400 hover:text-stone-700 underline"
                  data-testid="billing-remove-promo"
                >
                  remove
                </button>
              </div>
            )}
          </div>

          {pay && (pay.payfast_ready || pay.paypal_ready || pay.eft_ready) ? (
            <div className="space-y-3">
              {pay.payfast_ready && (
                <Button
                  onClick={payWithPayfast}
                  disabled={busy}
                  data-testid="checkout-payfast"
                  className="w-full rounded-full bg-stone-900 text-white hover:bg-stone-800 h-11"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  )}
                  Pay with PayFast
                </Button>
              )}
              {pay.paypal_ready && (
                <Button
                  onClick={payWithPaypal}
                  disabled={busy}
                  variant="outline"
                  data-testid="checkout-paypal"
                  className="w-full rounded-full h-11 border-2 border-[#003087] text-[#003087] hover:bg-[#003087]/5"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  )}
                  Pay with PayPal
                </Button>
              )}
              {pay.eft_ready && (
                <Button
                  onClick={payWithEft}
                  disabled={busy}
                  variant="outline"
                  data-testid="checkout-eft"
                  className="w-full rounded-full h-11 border-stone-300 text-stone-800 hover:bg-stone-50"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <Building2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  )}
                  Pay via EFT / Bank transfer
                </Button>
              )}

              {(eft || billing.eft_pending) && (() => {
                const ev = eft || billing.eft_pending;
                const processing = ev.status === "processing";
                return (
                <div
                  className="mt-2 rounded-xl border border-stone-200 bg-stone-50 p-5 space-y-3"
                  data-testid="eft-details"
                >
                  <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
                    Bank transfer details
                  </p>
                  <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                    {[
                      ["Bank", ev.bank?.bank_name],
                      ["Account holder", ev.bank?.account_name],
                      ["Account number", ev.bank?.account_number],
                      ["Branch code", ev.bank?.branch_code],
                      ["Account type", ev.bank?.account_type],
                      ["SWIFT / BIC", ev.bank?.swift],
                    ]
                      .filter(([, v]) => v)
                      .map(([k, v]) => (
                        <Fragment key={k}>
                          <dt className="text-stone-500">{k}</dt>
                          <dd className="text-stone-900 font-medium">{v}</dd>
                        </Fragment>
                      ))}
                    <dt className="text-stone-500">Amount</dt>
                    <dd className="text-stone-900 font-medium">
                      {ev.currency} {Number(ev.amount).toFixed(2)}
                      {ev.voucher_code && (
                        <span className="ml-2 text-xs text-emerald-700">({ev.voucher_code} applied)</span>
                      )}
                    </dd>
                  </dl>
                  <div className="rounded-lg bg-white border border-stone-200 p-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold mb-1">
                      Use this payment reference
                    </p>
                    <p
                      className="font-mono text-base text-stone-900 break-all"
                      data-testid="eft-reference"
                    >
                      {ev.reference}
                    </p>
                  </div>
                  {processing ? (
                    <>
                      <div
                        className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2.5 text-sm text-amber-800"
                        data-testid="eft-processing-banner"
                      >
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                        Payment is being processed — we'll activate your subscription once we
                        confirm receipt.
                      </div>
                      <Button
                        onClick={cancelEftPayment}
                        disabled={busy}
                        variant="outline"
                        data-testid="eft-cancel"
                        className="w-full rounded-full h-10 border-stone-300 text-stone-700 hover:bg-stone-100"
                      >
                        {busy ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                        ) : (
                          <XCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        )}
                        Cancel payment
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-stone-500">
                        Make the transfer using the reference above, then let us know.
                      </p>
                      <Button
                        onClick={claimEftPaid}
                        disabled={busy}
                        data-testid="eft-payment-made"
                        className="w-full rounded-full bg-stone-900 text-white hover:bg-stone-800 h-10"
                      >
                        {busy ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        )}
                        I've made the payment
                      </Button>
                      <Button
                        onClick={cancelEftPayment}
                        disabled={busy}
                        variant="ghost"
                        data-testid="eft-cancel"
                        className="w-full rounded-full h-9 text-stone-500 hover:text-stone-800 hover:bg-stone-100"
                      >
                        Cancel payment
                      </Button>
                    </>
                  )}
                </div>
                );
              })()}

              {pay.live_mode ? null : (
                <p className="text-xs text-amber-600 text-center" data-testid="billing-sandbox-note">
                  Sandbox mode — test payments only.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-stone-500" data-testid="billing-no-gateway">
              No payment method is available yet. The studio admin needs to configure a gateway.
            </p>
          )}
        </div>
      )}

      {/* Voucher redemption */}
      <div className="bg-white border border-stone-200 rounded-2xl p-6 md:p-8 space-y-4">
        <div className="flex items-center gap-2">
          <Ticket className="w-4 h-4 text-stone-700" strokeWidth={1.5} />
          <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
            Have a voucher code?
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={voucher}
            onChange={(e) => setVoucher(e.target.value.toUpperCase())}
            data-testid="billing-voucher-input"
            placeholder="ATELIER-2025"
            className="h-10 flex-1"
          />
          <Button
            onClick={redeem}
            disabled={busy}
            variant="outline"
            data-testid="billing-voucher-redeem"
            className="rounded-full"
          >
            Redeem
          </Button>
        </div>
        <p className="text-xs text-stone-500">
          Trial-extension vouchers apply immediately. Discount vouchers will pre-fill the promo field for checkout.
        </p>
      </div>
    </div>
  );
}
